import React, { useState, useEffect, useMemo, useRef } from 'react';
import useMarketData from '../hooks/useMarketData.js';

/**
 * CpiFfrSpreadWidget
 * ------------------
 * Compact live ribbon showing "PPI X% · CPI Y% · FFR Z% | Real Yield: W%"
 *
 * The four numbers tell a coherent macro story when read left-to-right:
 *   PPI YoY  → upstream producer-price pressure (BLS Final Demand)
 *   CPI YoY  → consumer-price inflation                              (BLS)
 *   FFR      → Fed Funds rate, the policy lever
 *   Real Yield = FFR − CPI YoY (negative = financial repression, BTC-bullish)
 *
 * Architecture choices that matter:
 *
 *   - Subscribes to the existing useMarketData hook instead of running its own
 *     fetch loop. Two timers polling the same FRED endpoints would just rate-
 *     limit each other and double the CloudWatch invocations on the proxy
 *     Lambda. Single source of truth = useMarketData.
 *
 *   - YoY CPI/PPI use *same calendar month last year* as the anchor (e.g. Apr
 *     2026 vs Apr 2025). Naïvely taking the 12th-back observation drifts
 *     when FRED data has a gap.
 *
 *   - "X seconds ago" timestamp ticks on its own 1-second cadence so it's
 *     fluid even though FRED data only refreshes every ~60 minutes.
 */
export default function CpiFfrSpreadWidget({ className = '' }) {
  const { fred, lastUpdated } = useMarketData();
  const [_, forceTick] = useState(0);

  // 1-second ticker — drives the "X sec ago" label without re-fetching
  useEffect(() => {
    const id = setInterval(() => forceTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // ── Derive display values from the live FRED state ──────────────────────
  // PPI is best-effort: if it isn't loaded yet (e.g. older FRED cache from
  // before PPIFIS was added) we still render CPI/FFR so the widget never
  // blanks. The ppi field will read "—" until the next cache refresh.
  const display = useMemo(() => {
    if (!fred?.cpiYoY?.length || !fred?.ffr?.length) return null;
    const cpiLatest = lastValid(fred.cpiYoY);
    const ffrLatest = lastValid(fred.ffr);
    const ppiLatest = lastValid(fred.ppiYoY ?? []);
    if (!cpiLatest || !ffrLatest) return null;
    return {
      cpi: cpiLatest.value,
      ffr: ffrLatest.value,
      ppi: ppiLatest?.value ?? null,
      cpiDate: cpiLatest.date,
      ffrDate: ffrLatest.date,
      ppiDate: ppiLatest?.date ?? null,
      realYield: ffrLatest.value - cpiLatest.value,
    };
  }, [fred?.cpiYoY, fred?.ffr, fred?.ppiYoY]);

  const ageMs = lastUpdated ? Date.now() - lastUpdated.getTime() : null;
  const isError = fred?.status === 'error' || fred?.status === 'stale';
  const isLoading = !display && fred?.status === 'loading';

  // ── Loading state ───────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className={`cpi-ffr-ribbon is-loading ${className}`}>
        <Pill tone="loading" label="Loading" />
        <div className="body shimmer" />
        <Timestamp text="Connecting…" />
        <Styles />
      </div>
    );
  }

  // ── Empty / error fallback (no display values ever resolved) ────────────
  if (!display) {
    return (
      <div className={`cpi-ffr-ribbon has-error ${className}`}>
        <Pill tone="error" label="Error" />
        <div className="body">FRED data unavailable</div>
        <Styles />
      </div>
    );
  }

  // ── Live render ─────────────────────────────────────────────────────────
  const { ppi, cpi, ffr, realYield } = display;
  const realYieldColor =
    Math.abs(realYield) < 0.005 ? '#facc15'
    : realYield < 0             ? '#f87171'
    :                             '#4ade80';

  // Pill tone:
  //   green pulse if fresh AND no error
  //   gray static if stale (>60s since last successful fetch)
  //   orange if there's an error AND we're showing cached values
  const pillTone = isError ? 'error'
    : (ageMs ?? Infinity) < 60_000 ? 'live'
    : 'stale';
  const pillLabel = isError ? 'Stale'
    : pillTone === 'live' ? 'Live'
    : 'Stale';

  return (
    <div className={`cpi-ffr-ribbon ${isError ? 'has-error' : ''} ${className}`}>
      <Pill tone={pillTone} label={pillLabel} />
      <div className="body">
        <span className="lbl-ppi">PPI</span>{' '}
        {ppi != null ? `${ppi.toFixed(2)}%` : <span className="muted">—</span>}
        <span className="dot-divider">·</span>
        <span className="lbl-cpi">CPI</span> {cpi.toFixed(2)}%
        <span className="dot-divider">·</span>
        <span className="lbl-ffr">FFR</span> {ffr.toFixed(2)}%
        <span className="divider">|</span>
        Real Yield:{' '}
        <span className="real-yield" style={{ color: realYieldColor }}>
          {realYield >= 0 ? '+' : ''}{realYield.toFixed(2)}%
        </span>
      </div>
      <Timestamp text={formatAge(ageMs)} />
      {isError && (
        <span
          className="warn-icon"
          title={`Live fetch failed: ${fred.error ?? 'unknown'} — showing last-known values`}
        >⚠</span>
      )}
      <Styles />
    </div>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────────────
function Pill({ tone, label }) {
  return (
    <span className={`live-pill tone-${tone}`}>
      <span className="dot" />
      <span>{label}</span>
    </span>
  );
}

function Timestamp({ text }) {
  return <div className="timestamp">{text}</div>;
}

// ─── Pure helpers ───────────────────────────────────────────────────────────
function lastValid(arr) {
  if (!arr?.length) return null;
  for (let i = arr.length - 1; i >= 0; i--) {
    const v = arr[i];
    if (v && Number.isFinite(v.value)) return v;
  }
  return null;
}

function formatAge(ms) {
  if (ms == null || !Number.isFinite(ms)) return 'Awaiting first fetch';
  const sec = Math.max(0, Math.floor(ms / 1000));
  if (sec < 60)   return `Updated ${sec} sec ago`;
  const min = Math.floor(sec / 60);
  if (min < 60)   return `Updated ${min} min ago`;
  const hr = Math.floor(min / 60);
  return `Updated ${hr} hr ago`;
}

// ─── Scoped styles ──────────────────────────────────────────────────────────
// Inlined as a <style> block so the component is self-contained — drop into
// any layout without needing the parent to import a CSS file.
function Styles() {
  return (
    <style>{`
      .cpi-ffr-ribbon {
        display: flex; align-items: center; gap: 16px; flex-wrap: wrap;
        background: rgba(22,33,62,0.6);
        border: 1px solid rgba(255,255,255,0.08);
        border-radius: 12px;
        padding: 12px 18px;
        color: #e2e8f0;
        font-family: 'Inter', system-ui, sans-serif;
        font-size: 14px;
        backdrop-filter: blur(8px);
        transition: border-color 0.3s;
      }
      .cpi-ffr-ribbon.has-error { border-color: rgba(251,146,60,0.4); }

      .cpi-ffr-ribbon .live-pill {
        display: inline-flex; align-items: center; gap: 6px;
        padding: 4px 10px;
        border-radius: 999px;
        font-size: 10px; font-weight: 700;
        letter-spacing: 0.12em; text-transform: uppercase;
        border: 1px solid;
        transition: all 0.3s;
      }
      .cpi-ffr-ribbon .live-pill.tone-live {
        background: rgba(74,222,128,0.10);
        border-color: rgba(74,222,128,0.35);
        color: #4ade80;
      }
      .cpi-ffr-ribbon .live-pill.tone-stale,
      .cpi-ffr-ribbon .live-pill.tone-loading {
        background: rgba(148,163,184,0.10);
        border-color: rgba(148,163,184,0.35);
        color: #94a3b8;
      }
      .cpi-ffr-ribbon .live-pill.tone-error {
        background: rgba(251,146,60,0.10);
        border-color: rgba(251,146,60,0.45);
        color: #fb923c;
      }
      .cpi-ffr-ribbon .live-pill .dot {
        width: 6px; height: 6px;
        border-radius: 50%;
        background: currentColor;
      }
      .cpi-ffr-ribbon .live-pill.tone-live .dot {
        animation: cpi-ffr-pulse 1.6s ease-in-out infinite;
      }
      @keyframes cpi-ffr-pulse {
        0%, 100% { opacity: 1;   transform: scale(1); }
        50%      { opacity: 0.4; transform: scale(0.85); }
      }
      .cpi-ffr-ribbon .body {
        flex: 1; min-width: 0;
        font-family: 'JetBrains Mono', ui-monospace, monospace;
        font-variant-numeric: tabular-nums lining-nums;
      }
      .cpi-ffr-ribbon .lbl-ppi { color: #fbbf24; font-weight: 600; }
      .cpi-ffr-ribbon .lbl-cpi { color: #f87171; font-weight: 600; }
      .cpi-ffr-ribbon .lbl-ffr { color: #60a5fa; font-weight: 600; }
      .cpi-ffr-ribbon .divider     { color: rgba(255,255,255,0.25); margin: 0 8px; }
      .cpi-ffr-ribbon .dot-divider { color: rgba(255,255,255,0.20); margin: 0 6px; }
      .cpi-ffr-ribbon .muted       { color: rgba(255,255,255,0.40); }
      .cpi-ffr-ribbon .real-yield { font-weight: 700; }

      .cpi-ffr-ribbon .timestamp {
        font-size: 10px; font-weight: 600;
        letter-spacing: 0.1em; text-transform: uppercase;
        color: #94a3b8;
        font-family: 'JetBrains Mono', ui-monospace, monospace;
        font-variant-numeric: tabular-nums lining-nums;
        white-space: nowrap;
      }

      .cpi-ffr-ribbon .warn-icon {
        color: #fb923c;
        cursor: help;
        font-size: 13px;
      }

      .cpi-ffr-ribbon.is-loading .body.shimmer {
        height: 14px; width: 280px;
        background: linear-gradient(90deg,
          rgba(255,255,255,0.04),
          rgba(255,255,255,0.10),
          rgba(255,255,255,0.04));
        background-size: 200% 100%;
        animation: cpi-ffr-shimmer 1.4s linear infinite;
        border-radius: 4px;
      }
      @keyframes cpi-ffr-shimmer {
        0%   { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
    `}</style>
  );
}
